import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  FolderOpen,
  MessageSquare,
  Star,
  TrendingUp,
  Users,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import { useCMS } from '@/context/CMSContext'
import AnimatedSection from '@/components/AnimatedSection'
import { adminUrl } from '@/lib/siteUrls'

const stats = [
  { name: 'Total Projects', value: '24', change: '+12%', trend: 'up', icon: FolderOpen, color: 'bg-blue-50 text-blue-600' },
  { name: 'New Messages', value: '8', change: '+3', trend: 'up', icon: MessageSquare, color: 'bg-amber-50 text-amber-600' },
  { name: 'Reviews', value: '15', change: '+5', trend: 'up', icon: Star, color: 'bg-green-50 text-green-600' },
  { name: 'Team Members', value: '4', change: '0', trend: 'neutral', icon: Users, color: 'bg-purple-50 text-purple-600' },
]

const recentActivity = [
  { action: 'New project added', item: 'Lekki Peninsula Master Plan', time: '2 hours ago' },
  { action: 'Message received', item: 'From Dr. Adebayo Johnson', time: '5 hours ago' },
  { action: 'Review approved', item: 'Northern Heights Construction', time: '1 day ago' },
  { action: 'Project updated', item: 'Eko Atlantic Boundary Survey', time: '2 days ago' },
]

export default function AdminDashboard() {
  const { messages, reviews, fetchProjects, fetchMessages, fetchReviews } = useCMS()

  useEffect(() => {
    fetchProjects()
    fetchMessages()
    fetchReviews()
  }, [fetchProjects, fetchMessages, fetchReviews])

  const unreadMessages = messages.filter(m => !m.read).length
  const pendingReviews = reviews.filter(r => !r.approved).length

  return (
    <div className="space-y-8">
      <AnimatedSection>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-black font-serif">Dashboard</h1>
            <p className="text-neutral-500 text-sm mt-1">Welcome back to Advict admin panel</p>
          </div>
        </div>
      </AnimatedSection>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <AnimatedSection key={stat.name} delay={index * 0.05}>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium ${
                  stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-neutral-500'
                }`}>
                  {stat.change}
                  {stat.trend === 'up' && <ArrowUpRight className="w-3 h-3" />}
                  {stat.trend === 'down' && <ArrowDownRight className="w-3 h-3" />}
                </div>
              </div>
              <div className="text-2xl font-bold text-black font-serif">{stat.value}</div>
              <div className="text-sm text-neutral-500">{stat.name}</div>
            </div>
          </AnimatedSection>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid md:grid-cols-3 gap-6">
        <AnimatedSection>
          <Link to={adminUrl('/messages')} className="block bg-white rounded-xl p-6 shadow-sm border border-neutral-100 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-neutral-500">Unread Messages</div>
                <div className="text-3xl font-bold text-black font-serif mt-1">{unreadMessages}</div>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </Link>
        </AnimatedSection>

        <AnimatedSection delay={0.05}>
          <Link to={adminUrl('/reviews')} className="block bg-white rounded-xl p-6 shadow-sm border border-neutral-100 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-neutral-500">Pending Reviews</div>
                <div className="text-3xl font-bold text-black font-serif mt-1">{pendingReviews}</div>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Link>
        </AnimatedSection>

        <AnimatedSection delay={0.1}>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-neutral-500">Growth</div>
                <div className="text-3xl font-bold text-black font-serif mt-1">+24%</div>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Recent Activity */}
      <AnimatedSection>
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100">
          <div className="px-6 py-4 border-b border-neutral-100">
            <h2 className="font-semibold text-black font-serif">Recent Activity</h2>
          </div>
          <div className="divide-y divide-neutral-100">
            {recentActivity.map((activity, index) => (
              <div key={index} className="px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                <div>
                  <div className="font-medium text-black text-sm">{activity.action}</div>
                  <div className="text-neutral-500 text-sm">{activity.item}</div>
                </div>
                <div className="text-neutral-400 text-xs">{activity.time}</div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>
    </div>
  )
}
